import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { opportunityService } from '../../services/opportunityService';
import './OpportunityDetail.css';

const OpportunityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadOpportunity();
  }, [id]);

  const loadOpportunity = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await opportunityService.getOpportunity(id);
      setOpportunity(data);
    } catch (error) {
      console.error('Error loading opportunity:', error);
      setError('Failed to load opportunity details.');
    } finally {
      setLoading(false);
    }
  };

  const handleWishlistToggle = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setActionLoading(true);
      
      if (opportunity.user_has_wishlisted) {
        await opportunityService.removeFromWishlist(opportunity.id);
        setOpportunity(prev => ({
          ...prev,
          user_has_wishlisted: false
        }));
      } else {
        await opportunityService.addToWishlist({ opportunity: opportunity.id });
        setOpportunity(prev => ({
          ...prev,
          user_has_wishlisted: true
        }));
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApply = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate(`/opportunities/${id}/apply`);
  };

  if (loading) {
    return (
      <div className="opportunity-detail">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading opportunity details...</p>
        </div>
      </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div className="opportunity-detail">
        <div className="error-container">
          <h2>❌ Error</h2>
          <p>{error || 'Opportunity not found.'}</p>
          <Link to="/opportunities" className="btn-outline">
            Back to Opportunities
          </Link>
        </div>
      </div>
    );
  }

  const deadlineUrgency = opportunityService.getDeadlineUrgency(opportunity.application_deadline);
  const isExpired = deadlineUrgency === 'expired';
  const canApply = opportunity.can_apply && !isExpired && !opportunity.user_has_applied;

  return (
    <div className="opportunity-detail">
      {/* Header */}
      <div className="detail-header">
        <div className="breadcrumb">
          <Link to="/opportunities">Opportunities</Link>
          <span>›</span>
          <span>{opportunity.title}</span>
        </div>

        <div className="header-content">
          <div className="header-main">
            <div className="opportunity-type">
              <span className="type-icon">
                {opportunityService.getTypeIcon(opportunity.opportunity_type)}
              </span>
              <span className="type-text">
                {opportunity.opportunity_type_display}
              </span>
              {opportunity.is_featured && (
                <span className="featured-badge">⭐ Featured</span>
              )}
            </div>

            <h1>{opportunity.title}</h1>

            <div className="header-meta">
              {opportunity.location && (
                <span className="meta-item">
                  📍 {opportunity.location}
                </span>
              )}
              <span className="meta-item">
                👥 {opportunity.available_capacity}/{opportunity.capacity} spots available
              </span>
              <span className={`deadline-info ${deadlineUrgency}`}>
                ⏰ Apply by {opportunityService.formatDeadline(opportunity.application_deadline)}
              </span>
            </div>

            <div className="capacity-bar">
              <div 
                className="capacity-fill" 
                style={{ width: `${opportunity.capacity_percentage}%` }}
              ></div>
              <span className="capacity-text">
                {opportunity.current_applications} of {opportunity.capacity} spots filled
              </span>
            </div>
          </div>

          <div className="header-actions">
            {user && (
              <button
                onClick={handleWishlistToggle}
                disabled={actionLoading}
                className={`wishlist-btn ${opportunity.user_has_wishlisted ? 'wishlisted' : ''}`}
              >
                {opportunity.user_has_wishlisted ? '💖 Wishlisted' : '🤍 Add to Wishlist'}
              </button>
            )}

            {canApply ? (
              <button onClick={handleApply} className="apply-btn">
                📝 Apply Now
              </button>
            ) : (
              <div className="apply-status">
                {!user ? (
                  <Link to="/login" className="btn-outline">
                    Login to Apply
                  </Link>
                ) : opportunity.user_has_applied ? (
                  <span className="status-applied">✅ Already Applied</span>
                ) : isExpired ? (
                  <span className="status-expired">⏰ Application Deadline Passed</span>
                ) : !opportunity.can_apply ? (
                  <span className="status-full">👥 Opportunity Full</span>
                ) : null}
              </div>
            )}

            {user && (user.is_staff || user.role === 'admin' || user.role === 'manager') && (
              <Link to={`/opportunities/${id}/edit`} className="edit-btn">
                ✏️ Edit
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="detail-content">
        <div className="content-main">
          <div className="content-section">
            <h2>📋 Description</h2>
            <div className="description-content">
              {opportunity.description.split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>

          {opportunity.requirements && (
            <div className="content-section">
              <h2>✅ Requirements & Qualifications</h2>
              <div className="requirements-content">
                {opportunity.requirements.split('\n').map((requirement, index) => (
                  <p key={index}>{requirement}</p>
                ))}
              </div>
            </div>
          )}

          {(opportunity.start_date || opportunity.end_date) && (
            <div className="content-section">
              <h2>📅 Important Dates</h2>
              <div className="dates-grid">
                <div className="date-item">
                  <strong>Application Deadline:</strong>
                  <span className={deadlineUrgency}>
                    {new Date(opportunity.application_deadline).toLocaleDateString()} at{' '}
                    {new Date(opportunity.application_deadline).toLocaleTimeString()}
                  </span>
                </div>
                {opportunity.start_date && (
                  <div className="date-item">
                    <strong>Start Date:</strong>
                    <span>{new Date(opportunity.start_date).toLocaleDateString()}</span>
                  </div>
                )}
                {opportunity.end_date && (
                  <div className="date-item">
                    <strong>End Date:</strong>
                    <span>{new Date(opportunity.end_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {opportunity.related_course && (
            <div className="content-section">
              <h2>🎓 Related Course</h2>
              <div className="related-course">
                <Link to={`/courses/${opportunity.related_course.id}`} className="course-link">
                  <div className="course-info">
                    <h4>{opportunity.related_course.title}</h4>
                    <p>{opportunity.related_course.description}</p>
                  </div>
                  <span className="course-arrow">→</span>
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="content-sidebar">
          <div className="sidebar-section">
            <h3>📊 Quick Info</h3>
            <div className="info-list">
              <div className="info-item">
                <span className="info-label">Type:</span>
                <span className="info-value">{opportunity.opportunity_type_display}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Capacity:</span>
                <span className="info-value">{opportunity.capacity} participants</span>
              </div>
              <div className="info-item">
                <span className="info-label">Available Spots:</span>
                <span className="info-value">{opportunity.available_capacity}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className={`info-value status-${opportunity.status}`}>
                  {opportunity.status_display}
                </span>
              </div>
              {opportunity.location && (
                <div className="info-item">
                  <span className="info-label">Location:</span>
                  <span className="info-value">{opportunity.location}</span>
                </div>
              )}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>🎯 Quick Actions</h3>
            <div className="action-list">
              <Link to="/opportunities" className="action-link">
                ← Back to All Opportunities
              </Link>
              <Link to={`/opportunities?type=${opportunity.opportunity_type}`} className="action-link">
                View Similar Opportunities
              </Link>
              {user && (
                <Link to="/opportunities/my-applications" className="action-link">
                  My Applications
                </Link>
              )}
            </div>
          </div>

          {opportunity.created_by && (
            <div className="sidebar-section">
              <h3>👤 Posted By</h3>
              <div className="creator-info">
                <p><strong>{opportunity.created_by.first_name} {opportunity.created_by.last_name}</strong></p>
                <p className="creator-role">{opportunity.created_by.role_display}</p>
                <p className="created-date">
                  Posted on {new Date(opportunity.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetail;
